import { FormEvent, useEffect, useState } from 'react'

import * as api from 'api/api'
import { AppSettings } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { SettingsTabs } from 'components/SettingsTabs'
import { Button, Card, ErrorState, SelectField, Spinner } from 'components/ui'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'
import { ForbiddenPage } from 'pages/system/ForbiddenPage'

const TIMEOUT_OPTIONS = [1, 4, 8, 24, 72, 168]

/** sessionTimeoutHours genuinely controls the JWT expiry (lib/auth.ts signToken) — not cosmetic.
 *  Two-step verification, SSO and per-device "active sessions" are left out: no 2FA/OAuth infra, and JWTs
 *  aren't session-tracked server-side (Figma: Admin / 35 · Settings — Security). */
export const SecuritySettingsPage = () => {
  const { user } = useAuth()
  const { data: settings, error, loading, reload } = useAsync(api.fetchAppSettings, [])
  const { busy, run } = useAction()
  const [form, setForm] = useState<AppSettings | null>(null)

  useEffect(() => {
    if (settings && !form) setForm(settings)
  }, [settings, form])

  if (!user?.isAdmin) return <ForbiddenPage message={t('إعدادات الأمان متاحة لمدير النظام فقط.', 'Security settings are available to Admin only.')} />

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!form) return
    const done = await run('security', () => api.updateAppSettings(form), t('تم حفظ إعدادات الأمان', 'Security settings saved'))
    if (done) reload()
  }

  const organisationName = settings?.organisationName ?? 'JoPath'
  const timeoutLabel = (hours: number) =>
    hours < 24
      ? t(`${hours} ساعة`, `${hours} ${hours === 1 ? 'hour' : 'hours'}`)
      : t(`${hours / 24} يوم`, `${hours / 24} ${hours / 24 === 1 ? 'day' : 'days'}`)

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('الإعدادات', 'Settings')}</h1>
          <p className="muted">
            {t(`${organisationName} · التغييرات تُطبّق على كل مشروع وتُحفظ تلقائياً`, `${organisationName} · Changes apply to every project and are saved automatically`)}
          </p>
        </div>
      </div>

      <SettingsTabs />

      {loading ? (
        <Spinner />
      ) : error || !form ? (
        <ErrorState message={error ?? t('تعذر تحميل الإعدادات', 'Could not load settings')} onRetry={reload} />
      ) : (
        <>
          <Card title={t('تسجيل الدخول', 'Sign-in')}>
            <form className="form-grid" onSubmit={save}>
              <SelectField
                label={t('تسجيل الخروج تلقائياً بعد', 'Sign out automatically after')}
                hint={t('مدة صلاحية الجلسة قبل أن يُطلب من المستخدم تسجيل الدخول مجدداً', 'How long a session lasts before the user has to sign in again')}
                value={form.sessionTimeoutHours}
                onChange={(event) => setForm({ ...form, sessionTimeoutHours: Number(event.target.value) })}
              >
                {TIMEOUT_OPTIONS.map((hours) => (
                  <option key={hours} value={hours}>
                    {timeoutLabel(hours)}
                  </option>
                ))}
              </SelectField>
              <div className="form-actions span-2">
                <Button type="submit" variant="primary" busy={busy === 'security'}>
                  {t('حفظ', 'Save')}
                </Button>
              </div>
            </form>
          </Card>

          <Card title={t('سجل الاعتمادات', 'Approval log')}>
            <p className="muted small">
              {t('تحميل سجل اعتماد وإرجاع وإرسال التقارير اليومية خلال آخر 12 شهراً (CSV).', 'Download the log of daily report approvals, returns, and submissions for the last 12 months (CSV).')}
            </p>
            <Button icon="download" onClick={() => api.downloadAuditLog()}>
              {t('تحميل السجل', 'Download log')}
            </Button>
          </Card>
        </>
      )}
    </div>
  )
}
